<script>
  import { t } from '../i18nStore';

  export let result;

  let copied = false;

  function formatCurrency(val) {
    if (val == null) return '0';
    return Number(val).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  function formatDateTime(dt) {
    const d = dt ? new Date(dt) : new Date();
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  function formatWarning(warn) {
    const matchSameBank = warn.match(/Only 1 account tier can be opened per bank \((.+?)\)\./);
    if (matchSameBank) {
      return $t('open_account.same_bank_warning', { bank: matchSameBank[1] });
    }
    return warn;
  }

  function copySummary() {
    const isCash = result.unit === '$';
    const rewardFormatted = `${isCash ? 'HK$' : ''}${formatCurrency(result.grandTotalReward)}${result.unit === 'miles' ? ' Miles' : ''}`;
    let text = `${$t('open_account.share_card.summary_title')}\n`;
    text += `${$t('open_account.share_card.calculated_on', { date: formatDateTime(result.calculationDate) })}\n`;
    text += `${$t('open_account.share_card.total_line', { money: formatCurrency(result.totalNewMoney) })}\n`;
    text += `${$t('open_account.share_card.allocated_line', { amount: formatCurrency(result.totalAllocated) })}\n`;
    if (result.remainingUnallocated > 0) {
      text += `${$t('open_account.share_card.unallocated_line', { amount: formatCurrency(result.remainingUnallocated) })}\n`;
    }
    text += `${$t('open_account.share_card.max_reward_line', { reward: rewardFormatted })}\n\n`;

    if (result.allocations && result.allocations.length > 0) {
      text += `${$t('open_account.share_card.strategy_header')}\n`;
      result.allocations.forEach(alloc => {
        const rewStr = `${isCash ? 'HK$' : ''}${formatCurrency(alloc.rewardEarned)}${result.unit === 'miles' ? ' Miles' : ''}`;
        text += `${$t('open_account.share_card.account_row', {
          bank: alloc.brand,
          name: alloc.promotionName,
          amount: formatCurrency(alloc.amountDeposited),
          reward: rewStr
        })}\n`;
      });
    }

    if (result.gainVsBestSingle > 0) {
      text += `\n✨ ${$t('open_account.share_card.gain_vs_single', {
        gain: `${isCash ? 'HK$' : ''}${formatCurrency(result.gainVsBestSingle)}${result.unit === 'miles' ? ' Miles' : ''}`,
        percent: result.percentGainVsBestSingle.toFixed(1)
      })}\n`;
    }

    text += `\n${$t('open_account.share_card.cta')}`;

    navigator.clipboard.writeText(text).then(() => {
      copied = true;
      setTimeout(() => (copied = false), 3000);
    });
  }
</script>

<div class="share-card">
  <!-- Card Header -->
  <div class="card-header">
    <div class="brand-left">
      <div class="brand-badge">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="3" y1="21" x2="21" y2="21"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
          <polygon points="12 2 2 7 22 7 12 2"></polygon>
          <line x1="7" y1="10" x2="7" y2="21"></line>
          <line x1="12" y1="10" x2="12" y2="21"></line>
          <line x1="17" y1="10" x2="17" y2="21"></line>
        </svg>
      </div>
      <div>
        <div class="brand-title">SNAP PERK</div>
        <div class="brand-subtitle">{$t('open_account.share_card.tagline')}</div>
      </div>
    </div>
    <div class="calc-date">
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
      </svg>
      <span>{formatDateTime(result.calculationDate)}</span>
    </div>
  </div>

  <div class="card-body">
    <!-- Main Metric Tiles -->
    <div class="metric-tiles">
      <div class="metric-box tax-box">
        <div class="metric-label">{$t('open_account.share_card.total_money')}</div>
        <div class="metric-value">HK$ {formatCurrency(result.totalNewMoney)}</div>
      </div>
      <div class="metric-box rebate-box">
        <div class="metric-label">
          {result.isCrossPromotion ? $t('open_account.share_card.optimized_reward') : $t('open_account.share_card.best_reward')}
        </div>
        <div class="metric-value green">
          {result.unit === '$' ? 'HK$' : ''}{formatCurrency(result.grandTotalReward)}{result.unit === 'miles' ? ' Miles' : ''}
        </div>
      </div>
    </div>

    <!-- Allocated vs Idle -->
    <div class="deposit-details-row">
      <div class="detail-item">
        <span class="detail-label">{$t('open_account.share_card.allocated')}:</span>
        <span class="detail-val">HK$ {formatCurrency(result.totalAllocated)}</span>
      </div>
      {#if result.remainingUnallocated > 0}
        <div class="detail-item unallocated">
          <span class="detail-label">{$t('open_account.share_card.unallocated')}:</span>
          <span class="detail-val orange">HK$ {formatCurrency(result.remainingUnallocated)}</span>
        </div>
      {/if}
    </div>

    <!-- Gain vs single badge -->
    {#if result.gainVsBestSingle > 0}
      <div class="bonus-badge">
        <span class="bonus-icon">✨</span>
        <span class="bonus-text">
          {$t('open_account.share_card.gain_vs_single', {
            gain: `${result.unit === '$' ? 'HK$' : ''}${formatCurrency(result.gainVsBestSingle)}${result.unit === 'miles' ? ' Miles' : ''}`,
            percent: result.percentGainVsBestSingle.toFixed(1)
          })}
        </span>
      </div>
    {/if}

    <hr class="divider" />

    <!-- Split Strategy Allocations -->
    <div class="section-title">{$t('open_account.share_card.allocation_strategy')}</div>

    {#if !result.allocations || result.allocations.length === 0}
      <div class="empty-alloc">{$t('open_account.empty_promos_msg')}</div>
    {:else}
      <div class="alloc-list">
        {#each result.allocations as alloc}
          <div class="alloc-card">
            <div class="alloc-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="3" y1="21" x2="21" y2="21"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
                <polygon points="12 2 2 7 22 7 12 2"></polygon>
                <line x1="7" y1="10" x2="7" y2="21"></line>
                <line x1="12" y1="10" x2="12" y2="21"></line>
                <line x1="17" y1="10" x2="17" y2="21"></line>
              </svg>
            </div>
            <div class="alloc-info">
              <div class="alloc-name">{alloc.brand} • {alloc.promotionName}</div>
              <div class="alloc-deposit">
                {$t('open_account.share_card.deposit_req', { amount: formatCurrency(alloc.amountDeposited) })}
                {#if alloc.remarks}
                  <span class="alloc-remarks"> • {alloc.remarks}</span>
                {/if}
              </div>
            </div>
            <div class="alloc-reward">
              +{result.unit === '$' ? 'HK$' : ''}{formatCurrency(alloc.rewardEarned)}{result.unit === 'miles' ? ' Miles' : ''}
            </div>
          </div>
        {/each}
      </div>
    {/if}

    <!-- Single Account Comparison Table -->
    {#if result.singlePromotionResults && result.singlePromotionResults.length > 0}
      <hr class="divider" />
      <div class="section-title">{$t('open_account.share_card.single_comparison')}</div>
      <div class="section-subtitle">{$t('open_account.share_card.single_comparison_desc')}</div>

      <div class="single-list">
        {#each result.singlePromotionResults.slice(0, 4) as single}
          {@const isTop = single.rewardEarned === result.bestSingleReward && single.rewardEarned > 0}
          <div class="single-row {isTop ? 'top-single' : ''}">
            <div class="single-name">
              {#if isTop}
                <span class="medal">🏆</span>
              {:else}
                <span class="dot">•</span>
              {/if}
              <span>{single.brand} ({single.promotionName})</span>
            </div>
            <div class="single-reward">
              {#if single.rewardEarned > 0}
                {result.unit === '$' ? 'HK$' : ''}{formatCurrency(single.rewardEarned)}{result.unit === 'miles' ? ' Miles' : ''}
              {:else}
                -
              {/if}
            </div>
          </div>
        {/each}
      </div>
    {/if}

    <!-- Warnings -->
    {#if result.warnings && result.warnings.length > 0}
      <div class="warnings-box">
        {#each result.warnings as warn}
          <div class="warning-row">
            <span class="warn-icon">ℹ️</span>
            <span>{formatWarning(warn)}</span>
          </div>
        {/each}
      </div>
    {/if}

    <!-- Action Button -->
    <div class="actions">
      <button class="share-btn" on:click={copySummary}>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="18" cy="5" r="3"></circle>
          <circle cx="6" cy="12" r="3"></circle>
          <circle cx="18" cy="19" r="3"></circle>
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
        </svg>
        <span>{copied ? $t('open_account.share_card.copied') : $t('open_account.share_card.share')}</span>
      </button>
    </div>

    <!-- Watermark footer -->
    <div class="watermark">
      {$t('open_account.share_card.watermark')}
    </div>
  </div>
</div>

<style>
  .share-card {
    background: #ffffff;
    border-radius: 16px;
    border: 1.5px solid rgba(15, 23, 42, 0.12);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04);
    overflow: hidden;
    margin: 20px 0;
  }

  .card-header {
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    padding: 14px 18px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .brand-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .brand-badge {
    background: rgba(212, 160, 23, 0.2);
    color: #f6c845;
    padding: 6px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .brand-title {
    color: #f6c845;
    font-weight: 800;
    font-size: 13px;
    letter-spacing: 1.2px;
  }

  .brand-subtitle {
    color: #ffffff;
    font-size: 11px;
    font-weight: 500;
  }

  .calc-date {
    display: flex;
    align-items: center;
    gap: 5px;
    background: rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.8);
    font-size: 11px;
    padding: 4px 10px;
    border-radius: 20px;
  }

  .card-body {
    padding: 18px;
  }

  .metric-tiles {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 12px;
  }

  .metric-box {
    padding: 12px 14px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
  }

  .tax-box {
    background: #f8fafc;
  }

  .rebate-box {
    background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
    border-color: #81c784;
  }

  .metric-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.6px;
    color: #64748b;
    margin-bottom: 4px;
  }

  .rebate-box .metric-label {
    color: #2e7d32;
  }

  .metric-value {
    font-size: 19px;
    font-weight: 800;
    color: #0f172a;
    word-break: break-all;
  }

  .metric-value.green {
    color: #1b5e20;
  }

  .deposit-details-row {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: #475569;
    margin-bottom: 10px;
    font-weight: 600;
  }

  .unallocated .orange {
    color: #d97706;
  }

  .bonus-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #fff8e1;
    border: 1px solid #ffd54f;
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 700;
    color: #b45309;
    margin-bottom: 14px;
  }

  .divider {
    border: none;
    border-top: 1px solid #e2e8f0;
    margin: 14px 0;
  }

  .section-title {
    font-size: 11px;
    font-weight: 800;
    color: #64748b;
    letter-spacing: 0.8px;
    margin-bottom: 4px;
  }

  .section-subtitle {
    font-size: 11px;
    color: #94a3b8;
    margin-bottom: 10px;
  }

  .alloc-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
  }

  .alloc-card {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 10px 14px;
  }

  .alloc-icon {
    background: rgba(15, 23, 42, 0.08);
    color: #0f172a;
    padding: 6px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .alloc-info {
    flex: 1;
  }

  .alloc-name {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
  }

  .alloc-deposit {
    font-size: 11px;
    color: #64748b;
    margin-top: 2px;
  }

  .alloc-reward {
    font-size: 14px;
    font-weight: 800;
    color: #2e7d32;
    white-space: nowrap;
  }

  .single-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 8px;
  }

  .single-row {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: #334155;
    padding: 4px 6px;
    border-radius: 6px;
  }

  .single-row.top-single {
    font-weight: 700;
    background: #f0fdf4;
    color: #166534;
  }

  .single-name {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .warnings-box {
    margin-top: 14px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .warning-row {
    display: flex;
    align-items: center;
    gap: 6px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 11px;
    color: #92400e;
  }

  .actions {
    margin-top: 16px;
  }

  .share-btn {
    width: 100%;
    background: #0f172a;
    color: #ffffff;
    border: none;
    border-radius: 10px;
    padding: 12px;
    font-weight: 700;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    cursor: pointer;
    transition: background 0.2s;
  }

  .share-btn:hover {
    background: #1e293b;
  }

  .watermark {
    margin-top: 12px;
    text-align: center;
    font-size: 10px;
    color: #94a3b8;
  }
</style>
